/**
 * Image Popup Modal Component
 *
 * A simple popup modal that displays an image with optional email capture.
 * Supports delay, show-once behavior, and localStorage persistence.
 */

class ImagePopupModal extends HTMLElement {
  constructor() {
    super();
    this.apparitionDelay = parseInt(this.getAttribute('apparition-delay') || '3');
    this.onlyOnce = this.hasAttribute('only-once');
    this.storageKey = 'theme:image-popup-appeared';
    this.isOpen = false;
    this.timeoutId = null;
  }

  connectedCallback() {
    // Don't show if already appeared and onlyOnce is enabled
    if (this.onlyOnce && localStorage.getItem(this.storageKey)) {
      return;
    }

    // Open immediately if form was submitted (has 'open' attribute)
    if (this.hasAttribute('open')) {
      this.open();
      return;
    }

    // Schedule popup to appear after delay
    this.timeoutId = setTimeout(() => {
      this.open();
    }, this.apparitionDelay * 1000);

    // Setup close button
    this.setupCloseButton();

    // Close on backdrop click
    this.addEventListener('click', (e) => {
      if (e.target === this) {
        this.close();
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  disconnectedCallback() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  setupCloseButton() {
    // Create close button if it doesn't exist
    if (!this.querySelector('.modal__close-button')) {
      const closeButton = document.createElement('button');
      closeButton.className = 'absolute top-4 right-4 z-10 bg-white rounded-full w-10 h-10 flex items-center justify-center cursor-pointer border-0 shadow-md hover:scale-110 transition-transform tap-area';
      closeButton.setAttribute('type', 'button');
      closeButton.setAttribute('aria-label', 'Close');
      closeButton.innerHTML = `
        <svg class="w-4 h-4" viewBox="0 0 16 16" width="16" height="16">
          <path d="M1 1l14 14M15 1L1 15" stroke="currentColor" stroke-width="2" fill="none"/>
        </svg>
      `;
      this.prepend(closeButton);

      closeButton.addEventListener('click', () => {
        this.close();
      });
    }
  }

  open() {
    this.isOpen = true;
    this.setAttribute('open', '');
    this.classList.remove('hidden');
    this.classList.add('flex');
    document.body.style.overflow = 'hidden';

    // Mark as appeared in localStorage
    if (this.onlyOnce) {
      localStorage.setItem(this.storageKey, 'true');
    }

    // Trigger animation
    requestAnimationFrame(() => {
      this.classList.remove('opacity-0');
      this.classList.add('opacity-100');
    });
  }

  close() {
    this.isOpen = false;
    this.classList.remove('opacity-100');
    this.classList.add('opacity-0');

    setTimeout(() => {
      this.removeAttribute('open');
      this.classList.remove('flex');
      this.classList.add('hidden');
      document.body.style.overflow = '';
    }, 300);
  }
}

// Button to open image popup from header
class ImagePopupButton extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('button');
    if (button) {
      button.addEventListener('click', () => {
        const modal = document.querySelector('image-popup-modal');
        if (modal) {
          modal.open();
        }
      });
    }
  }
}

// Register custom elements
if (!window.customElements.get('image-popup-modal')) {
  window.customElements.define('image-popup-modal', ImagePopupModal);
}

if (!window.customElements.get('image-popup-button')) {
  window.customElements.define('image-popup-button', ImagePopupButton);
}

export { ImagePopupModal, ImagePopupButton };
